import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  // 'annual' ili 'monthly' — default ostaje 'annual' (postojeće ponašanje / t('pricing.year'))
  const [billingCycle, setBillingCycle] = useState('annual');

  const getPrice = (planName) => {
    const lang = i18n.language;
    const pricingTable = {
    annual: {
  Basic: { bs: "150 KM", it: "75 €", de: "75 €", en: "75 €" },
  Premium: { bs: "450 KM", it: "230 €", de: "230 €", en: "230 €" },
  Enterprise: { bs: "1200 KM", it: "615 €", de: "615 €", en: "615 €" }
},
monthly: {
  Basic: { bs: "15,99 KM", it: "7,99 €", de: "7,99 €", en: "7.99 €" },
  Premium: { bs: "44,99 KM", it: "24,99 €", de: "24,99 €", en: "24.99 €" },
  Enterprise: { bs: "119,99 KM", it: "59,99 €", de: "59,99 €", en: "59.99 €" }
}
    };
    const table = pricingTable[billingCycle] || pricingTable.annual;
    return table[planName][lang] || table[planName]['en'];
  };

  // Labela za period pored cijene ('/ godina' ili '/ mjesec') u skladu sa billingCycle-om
  const getPeriodLabel = () => {
    return billingCycle === 'annual'
      ? t('pricing.year', { defaultValue: 'godina' })
      : t('pricing.month', { defaultValue: 'mjesec' });
  };

  const plans = [
    {
      name: "Basic",
      icon: "User",
      features: t('pricing.basic_features', { returnObjects: true }),
      description: t('pricing.descriptions.Basic'),
      color: "border-border"
    },
    {
      name: "Premium",
      icon: "ShieldCheck",
      features: t('pricing.premium_features', { returnObjects: true }),
      description: t('pricing.descriptions.Premium'),
      color: "border-primary shadow-xl shadow-primary/10",
      popular: true
    },
    {
      name: "Enterprise",
      icon: "Zap",
      features: t('pricing.enterprise_features', { returnObjects: true }),
      description: t('pricing.descriptions.Enterprise'),
      color: "border-border"
    }
  ];

  // Routing logika: ruta zavisi i od odabranog paketa i od billingCycle-a.
  // Annual -> /basic-pack, /premium-pack, /enterprise-pack (postojeće rute, nepromijenjeno)
  // Monthly -> /basic-pack-monthly, /premium-pack-monthly, /enterprise-pack-monthly (nove rute)
  const handleConfirm = () => {
    if (!selectedPlan) return;

    const routeMap = {
      annual: {
        Basic: '/basic-pack',
        Premium: '/premium-pack',
        Enterprise: '/enterprise-pack'
      },
      monthly: {
        Basic: '/basic-pack-monthly',
        Premium: '/premium-pack-monthly',
        Enterprise: '/enterprise-pack-monthly'
      }
    };

    const targetRoute = routeMap[billingCycle]?.[selectedPlan.name];

    if (targetRoute) {
      navigate(targetRoute);
    }

    setSelectedPlan(null);
  };

  const Modal = () => {
    if (!selectedPlan) return null;

    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div 
          className="fixed inset-0 bg-background/40 backdrop-blur-md animate-in fade-in duration-300"
          onClick={() => setSelectedPlan(null)}
        />
        
        <div className="relative bg-card border border-border w-full max-w-lg rounded-3xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.5)] overflow-hidden animate-in zoom-in-95 duration-200">
          <div className="p-8 space-y-6 text-left">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3 text-primary">
                <Icon name={selectedPlan.icon} size={28} />
                <h2 className="text-2xl font-black uppercase tracking-tight text-foreground">{selectedPlan.name}</h2>
              </div>
              <button 
                onClick={() => setSelectedPlan(null)} 
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-all"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-muted-foreground leading-relaxed border-l-4 border-primary/30 pl-4 py-1 bg-primary/5 rounded-r-lg">
                {selectedPlan.description}
              </p>

              {/* Cijena u modalu, u skladu sa trenutno odabranim billing cycle-om */}
              <div className="flex items-baseline gap-2 px-1">
                <span className="text-2xl font-black text-foreground">{getPrice(selectedPlan.name)}</span>
                <span className="text-muted-foreground text-xs font-bold uppercase">/ {getPeriodLabel()}</span>
              </div>
              
              <div className="h-px bg-border w-full" />
              
              <h4 className="font-bold text-[10px] uppercase tracking-[0.2em] text-primary">
                {t('pricing.what_you_get')}
              </h4>
              <ul className="grid grid-cols-1 gap-3">
                {Array.isArray(selectedPlan.features) && selectedPlan.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-semibold text-foreground/80">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                       <Icon name="Check" size={12} className="text-emerald-500" />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-4 flex gap-3">
              <button 
                onClick={() => setSelectedPlan(null)}
                className="flex-1 py-4 bg-muted hover:bg-muted/80 text-foreground rounded-2xl font-bold uppercase text-[10px] tracking-widest transition-all border border-transparent hover:border-border"
              >
                {t('pricing.close')}
              </button>
              
              <button 
                onClick={handleConfirm}
                className="flex-[2] py-4 bg-primary text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-lg shadow-primary/20 hover:brightness-110 hover:scale-[1.02] active:scale-95 transition-all"
              >
                {t('pricing.confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <section className="relative w-full min-h-[80vh] flex items-center justify-center px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
      <div className="max-w-6xl mx-auto text-center space-y-10">
        
        <div className="flex flex-col items-center justify-center mb-10 animate-fade-in">
          <div className="relative flex items-center justify-center text-primary">
            <Icon name="Shield" size={56} strokeWidth={1.6} />
            <span className="absolute inset-0 flex items-center justify-center font-black text-2xl" style={{ transform: 'translateY(-5%)' }}>O</span>
          </div>
          <span className="text-primary font-bold uppercase tracking-widest text-xs mt-2 select-none">Desk</span>
        </div>

        <div className="space-y-4">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground leading-tight tracking-tight">
            {t('hero.welcome')} <span className="text-primary">OrizonDesk</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed ">
            {t('hero.subtitle')}
          </p>
        </div>

        {/* TOGGLE PREKIDAČ: Mjesečno / Godišnje */}
        <div className="flex items-center justify-center pt-6">
          <div className="relative inline-flex items-center bg-muted rounded-full p-1.5 border border-border shadow-sm">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`relative z-10 px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-300 ${
                billingCycle === 'monthly'
                  ? 'bg-primary text-white shadow-md'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('pricing.monthly', { defaultValue: 'Mjesečno' })}
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('annual')}
              className={`relative z-10 px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-2 ${
                billingCycle === 'annual'
                  ? 'bg-primary text-white shadow-md'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('pricing.annual', { defaultValue: 'Godišnje' })}
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full transition-all ${
                billingCycle === 'annual'
                  ? 'bg-white/20 text-white'
                  : 'bg-emerald-500/10 text-emerald-600'
              }`}>
                {t('pricing.save_discount', { defaultValue: 'Uštedite 20%' })}
              </span>
            </button>
          </div>
        </div>

        {/* PRICING GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-10">
          {plans.map((plan, idx) => (
            <div key={idx} className={`relative bg-card border-2 ${plan.color} rounded-3xl p-8 flex flex-col transition-all duration-300 hover:scale-[1.02] shadow-sm hover:shadow-lg`}>
              <button 
                onClick={() => setSelectedPlan(plan)}
                className="absolute top-4 right-4 text-muted-foreground hover:text-primary transition-all p-2 hover:bg-primary/5 rounded-full"
              >
                <Icon name="Info" size={20} />
              </button>
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] font-black uppercase px-4 py-1 rounded-full tracking-widest shadow-md">
                  {t('pricing.popular')}
                </span>
              )}
              <div className="mb-4 flex justify-center text-primary">
                <Icon name={plan.icon} size={32} />
              </div>
              <h3 className="text-xl font-black uppercase tracking-wider mb-2">{plan.name}</h3>
              <div className="mb-6">
                <span className="text-4xl font-black">{getPrice(plan.name)}</span>
                <span className="text-muted-foreground text-xs font-bold uppercase"> / {getPeriodLabel()}</span>
              </div>
              <ul className="space-y-3 mb-8 text-left flex-1">
                {Array.isArray(plan.features) && plan.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground font-medium">
                    <Icon name="Check" size={14} className="text-emerald-500" /> {f}
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => setSelectedPlan(plan)}
                className="group w-full py-4 bg-primary text-white border-2 border-primary rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all duration-300 hover:bg-transparent hover:text-primary shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
              >
                 <span>{t('pricing.choose_plan')}</span>
                 <Icon name="ArrowRight" size={14} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-16 p-8 bg-primary/5 border border-primary/20 rounded-[2.5rem] max-w-2xl mx-auto transition-all hover:bg-primary/[0.08]">
          <div className="flex flex-col items-center gap-5">
            <div className="flex items-center gap-2 px-4 py-1.5 bg-primary/10 text-primary rounded-full text-[10px] font-black uppercase tracking-[0.15em]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              {t('pricing.badge_text')}
            </div>
            
            <div className="space-y-2">
              <h3 className="text-2xl md:text-3xl font-black text-foreground tracking-tight">
                {t('pricing.free_trial_btn')}
              </h3>
              <p className="text-sm text-muted-foreground font-medium italic">
                {t('pricing.free_trial_subtitle')}
              </p>
            </div>

            <a 
              href="/test"
              className="mt-2 px-10 py-5 bg-foreground text-background hover:bg-primary hover:text-white transition-all duration-500 rounded-2xl font-black uppercase text-[11px] tracking-[0.25em] shadow-2xl hover:shadow-primary/40 active:scale-95 flex items-center gap-3"
            >
              {t('pricing.start_test_btn')}
              <Icon name="Zap" size={16} />
            </a>
          </div>
        </div>

        <Modal />

        <div className="flex flex-col items-center pt-8">
          <p className="text-sm text-muted-foreground animate-pulse">
            {t('hero.gdpr_note')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;